// Catalogue read API contracts (`GET /catalogue/*`). Wire format: snake_case fields,
// quantities as decimal strings, instants as ISO-8601 UTC strings. State rule states are
// the domain document (`@enp/domain/catalogue` `stateRuleStateSchema`) as stored.
import { z } from 'zod';
import { allergenSchema } from '../production/allergens.js';
import { COST_SOURCES } from '../production/costing.js';
import {
  deductionModeSchema,
  familyRoleSchema,
  identifierKindSchema,
  productRoleSchema,
  recipeVersionStatusSchema,
  serviceKindSchema,
  stateKeySchema,
  stateRuleStateSchema,
} from '../catalogue/index.js';
import { qtyString } from '../quantity/index.js';
import { uomCode } from '../uom/index.js';

const instant = z.iso.datetime({ offset: true });

/** Largest page `GET /catalogue/products` returns. */
export const CATALOGUE_PAGE_MAX = 200;
/** Page size when `limit` is not given. */
export const CATALOGUE_PAGE_DEFAULT = 50;

/** Which rows by archive state: live only (default), archived only, or both. */
export const ARCHIVED_FILTERS = ['exclude', 'only', 'include'] as const;
/** zod schema for the `archived` query parameter. */
export const archivedFilterSchema = z.enum(ARCHIVED_FILTERS).default('exclude');

/**
 * Orders `GET /catalogue/products` can return (keyset pagination follows the order):
 * `name` A–Z (default), `-name` Z–A, `-updated` most recently changed first, `updated`
 * least recently changed first. Ties break on the product id.
 */
export const CATALOGUE_SORTS = ['name', '-name', '-updated', 'updated'] as const;
/** One of {@link CATALOGUE_SORTS}. */
export type CatalogueSort = (typeof CATALOGUE_SORTS)[number];

/** `:id` path parameter of catalogue routes. */
export const catalogueIdParamsSchema = z.object({ id: z.uuid() });

/**
 * `GET /catalogue/products` query.
 * - `q`: matches the name (case-insensitive substring), a current SKU by the prefix of its
 *   canonical form (`gf-lo` finds `GF-LOAF-WHT`), or a current GTIN (any valid length).
 * - `role`, `category_id`: filters. `archived`: see {@link ARCHIVED_FILTERS}.
 * - `state_tracked`: `true` = products with expiry states (their own state rule or their
 *   category's), `false` = products without.
 * - `sort`: see {@link CATALOGUE_SORTS}.
 * - `limit` ≤ {@link CATALOGUE_PAGE_MAX}; `cursor` is the previous page's `next_cursor`
 *   (valid only with the same filters and sort).
 */
export const catalogueProductListQuerySchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  role: productRoleSchema.optional(),
  category_id: z.uuid().optional(),
  archived: archivedFilterSchema,
  state_tracked: z.enum(['true', 'false']).optional(),
  /**
   * `true`: only products that need someone (a recipe waiting for the baker's sign-off, or a
   * sales-channel link to review).
   */
  attention: z.enum(['true']).optional(),
  sort: z.enum(CATALOGUE_SORTS).default('name'),
  limit: z.coerce.number().int().min(1).max(CATALOGUE_PAGE_MAX).default(CATALOGUE_PAGE_DEFAULT),
  cursor: z.string().min(1).max(512).optional(),
});
/** Parsed `GET /catalogue/products` query. */
export type CatalogueProductListQuery = z.infer<typeof catalogueProductListQuerySchema>;

/** A category reference. */
export const catalogueCategoryRefSchema = z.object({ id: z.uuid(), name: z.string() });

/** Why a product can never be offered on a channel (`null` = it may be). */
export const channelVetoInfoSchema = z
  .object({ code: z.literal('not_sellable'), reason: z.string() })
  .nullable();

/** The state rule that applies to a product, in brief: its own, or its category's. */
export const catalogueStateRuleRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  source: z.enum(['product', 'category']),
});

/**
 * Where a product's recipe stands: `active` (a signed-off version is in use), `draft` (only
 * drafts waiting for the baker's sign-off), `none` (no recipe, or only retired versions).
 */
export const RECIPE_STATES = ['active', 'draft', 'none'] as const;

/** A product in a list. */
export const catalogueProductSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  roles: z.array(productRoleSchema),
  base_uom: uomCode,
  tracks_expiry: z.boolean(),
  deduction_mode: deductionModeSchema,
  category: catalogueCategoryRefSchema.nullable(),
  family_id: z.uuid().nullable(),
  /** Current SKUs, canonical form. */
  skus: z.array(z.string()),
  /** Current GTINs, 14 digits. */
  gtins: z.array(z.string()),
  channel_veto: channelVetoInfoSchema,
  archived_at: instant.nullable(),
  /** The expiry states that apply, if any. */
  state_rule: catalogueStateRuleRefSchema.nullable(),
  /** See {@link RECIPE_STATES}. */
  recipe_state: z.enum(RECIPE_STATES),
  /** Current identifiers of every kind (SKUs, GTINs, channel ids, imported ids…). */
  identifier_count: z.int().min(0),
  updated_at: instant,
});
/** A product in a list. */
export type CatalogueProductSummary = z.infer<typeof catalogueProductSummarySchema>;

/** `GET /catalogue/products` response. */
export const catalogueProductListResponseSchema = z.object({
  items: z.array(catalogueProductSummarySchema),
  /** Pass as `cursor` for the next page; `null` on the last page. */
  next_cursor: z.string().nullable(),
  /** Products matching the search and filters, across every page. */
  total: z.int().min(0),
});
/** Response of `GET /catalogue/products`. */
export type CatalogueProductListResponse = z.infer<typeof catalogueProductListResponseSchema>;

/** A product identifier, current or historical. */
export const catalogueIdentifierSchema = z.object({
  id: z.uuid(),
  kind: identifierKindSchema,
  value: z.string(),
  canonical_value: z.string(),
  channel_id: z.uuid().nullable(),
  /** The channel's name when the identifier belongs to one. */
  channel_name: z.string().nullable(),
  valid_from: instant,
  /** `null` while current. */
  valid_to: instant.nullable(),
});

/** A member of a product family. */
export const catalogueFamilyMemberSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  family_role: familyRoleSchema,
  roles: z.array(productRoleSchema),
  archived_at: instant.nullable(),
});

/** `GET /catalogue/families/:id` response (also embedded in product detail). */
export const catalogueFamilySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  archived_at: instant.nullable(),
  members: z.array(catalogueFamilyMemberSchema),
});
/** A product family with its members. */
export type CatalogueFamily = z.infer<typeof catalogueFamilySchema>;

/** The state rule that applies to a product. */
export const catalogueStateRuleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  applies_to: z.enum(['product', 'category']),
  category_id: z.uuid().nullable(),
  /** Where the product gets it from: its own rule, or its category's. */
  source: z.enum(['product', 'category']),
  states: z.array(stateRuleStateSchema),
});

/**
 * A quantity worth the baker's second look before sign-off (`precisionWarnings` in
 * `@enp/domain/catalogue`), computed by the server with the component's base unit and every
 * conversion that applies. Never blocking.
 */
export const catalogueRecipePrecisionWarningSchema = z.discriminatedUnion('code', [
  z.object({ code: z.literal('whole_number_in_fractional_unit'), qty: qtyString, uom: uomCode }),
  z.object({
    code: z.literal('conversion_rounding'),
    qty: qtyString,
    uom: uomCode,
    base_uom: uomCode,
    base_qty: qtyString,
    round_trip: qtyString,
  }),
  z.object({ code: z.literal('missing_conversion'), uom: uomCode, base_uom: uomCode }),
]);
/** A precision warning on a recipe line. */
export type CatalogueRecipePrecisionWarning = z.infer<typeof catalogueRecipePrecisionWarningSchema>;

/** One component line of a recipe version. */
export const catalogueRecipeLineSchema = z.object({
  id: z.uuid(),
  position: z.int().min(1),
  component: z.object({ id: z.uuid(), name: z.string(), base_uom: uomCode }),
  qty: qtyString,
  uom: uomCode,
  loss_pct: qtyString,
  allowed_states: z.array(stateKeySchema),
  precision_warnings: z.array(catalogueRecipePrecisionWarningSchema),
});

/** A recipe version with its lines. */
export const catalogueRecipeVersionSchema = z.object({
  id: z.uuid(),
  version_no: z.int().min(1),
  status: recipeVersionStatusSchema,
  batch_yield_qty: qtyString,
  yield_uom: uomCode,
  instructions: z.string().nullable(),
  allergen_statement: z.string().nullable(),
  signed_off_at: instant.nullable(),
  effective_from: instant.nullable(),
  lines: z.array(catalogueRecipeLineSchema),
});

/** A recipe version with its lines. */
export type CatalogueRecipeVersion = z.infer<typeof catalogueRecipeVersionSchema>;

/** A recipe with its versions, newest first. */
export const catalogueRecipeSchema = z.object({
  id: z.uuid(),
  archived_at: instant.nullable(),
  versions: z.array(catalogueRecipeVersionSchema),
});

/** A channel mapping of the product. */
export const catalogueChannelMappingSchema = z.object({
  id: z.uuid(),
  channel: z.object({ id: z.uuid(), name: z.string(), kind: z.string() }),
  /** State key, or `null` for the product in any sellable state. */
  state: z.string().nullable(),
  external_item_id: z.string(),
  external_variant_id: z.string().nullable(),
  match_method: z.enum(['identifier', 'manual']),
  matched_by_identifier_id: z.uuid().nullable(),
  status: z.enum(['active', 'review', 'vetoed']),
  status_reason: z.string().nullable(),
  archived_at: instant.nullable(),
});

/** A unit conversion that applies to the product (its own, or account-wide). */
export const catalogueUomConversionSchema = z.object({
  id: z.uuid(),
  product_id: z.uuid().nullable(),
  from_uom: uomCode,
  to_uom: uomCode,
  factor: qtyString,
});

/** `GET /catalogue/products/:id` response. */
export const catalogueProductDetailSchema = catalogueProductSummarySchema.extend({
  shelf_life_days: z.int().min(0).nullable(),
  vat_rate_code: z.string(),
  /** What a `service` product is (B-031); null when not stated or not a service. */
  service_kind: serviceKindSchema.nullable().optional(),
  allergen_statement: z.string().nullable(),
  family_role: familyRoleSchema.nullable(),
  created_at: instant,
  updated_at: instant,
  /** Every identifier, current first, then history. */
  identifiers: z.array(catalogueIdentifierSchema),
  family: catalogueFamilySchema.nullable(),
  state_rule: catalogueStateRuleSchema.nullable(),
  recipes: z.array(catalogueRecipeSchema),
  channel_mappings: z.array(catalogueChannelMappingSchema),
  uom_conversions: z.array(catalogueUomConversionSchema),
  /** Declared allergens (M5). */
  allergens: z.array(allergenSchema).optional(),
  /**
   * The allergens the ingredients of its recipe's version in use (else its latest draft)
   * contain, nested recipes included; empty without a recipe. The page calls out any the
   * product doesn't declare, never "None declared" (UI8A-1, as the recipe page: UI5A-1).
   */
  ingredient_allergens: z.array(allergenSchema).optional(),
  /** Standard cost per base unit, whole cents (M4/M5), and where it came from. */
  standard_cost_cents: z.int().nullable().optional(),
  cost_source: z.enum(COST_SOURCES).nullable().optional(),
  /** Fields a person wrote last: an import asks before changing them (D-032). */
  person_owned_fields: z.array(z.string()).optional(),
});
/** Response of `GET /catalogue/products/:id`. */
export type CatalogueProductDetail = z.infer<typeof catalogueProductDetailSchema>;

/** `GET /catalogue/categories` query. */
export const catalogueCategoryListQuerySchema = z.object({ archived: archivedFilterSchema });

/** `GET /catalogue/categories` response. */
export const catalogueCategoryListResponseSchema = z.object({
  items: z.array(
    catalogueCategoryRefSchema.extend({
      archived_at: instant.nullable(),
      /** Live (not archived) products in the category. */
      product_count: z.int().min(0),
    }),
  ),
});
/** Response of `GET /catalogue/categories`. */
export type CatalogueCategoryListResponse = z.infer<typeof catalogueCategoryListResponseSchema>;
